/**
 * Reducer da chamada.
 *
 * Existe um único estado de chamada e um único conjunto de ações. O roteiro
 * simulado e os eventos da Realtime API despacham exatamente as mesmas ações —
 * é isso que faz a tela ser idêntica nos dois modos, e é o que permite trocar
 * de "simulada" para "WebRTC" sem reescrever painel nenhum.
 */

import type {
  Artifact,
  ArtifactStatus,
  AuditEntry,
  Effect,
  GuardrailState,
  Mark,
  Modality,
  Proposal,
  ReasoningCard,
  Speaker,
  ToolCall,
  Turn,
} from './types'

export type OrbState = 'idle' | 'ai' | 'cli' | 'think'

export interface CallState {
  phase: 'briefing' | 'live' | 'ended'
  elapsed: number
  turns: Turn[]
  modality: Modality
  capPct: number
  offerPct: number | null
  offerWall: boolean
  clientAskPct: number | null
  /** O primeiro pedido do cliente, preservado para o resumo pós-chamada:
   *  "pediu 50%, fechou em 12%" é a métrica que interessa, não o último pedido. */
  clientFirstAskPct: number | null
  trail: number[]
  proposal: Proposal | null
  artifacts: Artifact[]
  reasoning: ReasoningCard[]
  toolCalls: ToolCall[]
  audit: AuditEntry[]
  guardrails: GuardrailState[]
  humanInControl: boolean
  muted: boolean
  costBrl: number
  orb: OrbState
  jitterMs: number
}

export const initialCallState: CallState = {
  phase: 'briefing',
  elapsed: 0,
  turns: [],
  modality: 'parc',
  capPct: 12,
  offerPct: null,
  offerWall: false,
  clientAskPct: null,
  clientFirstAskPct: null,
  trail: [],
  proposal: null,
  artifacts: [],
  reasoning: [],
  toolCalls: [],
  audit: [],
  guardrails: [],
  humanInControl: false,
  muted: false,
  costBrl: 0.06,
  orb: 'idle',
  jitterMs: 0,
}

export type CallAction =
  | { type: 'reset'; guardrails: GuardrailState[]; capPct: number }
  | { type: 'start'; guardrails: GuardrailState[]; capPct: number }
  | { type: 'end' }
  | { type: 'tick' }
  /** No modo espelho o relógio é o do aparelho, não o do console. */
  | { type: 'syncElapsed'; elapsed: number }
  | { type: 'orb'; state: OrbState }
  /** Abre um novo balão. */
  | { type: 'beginTurn'; speaker: Speaker; at: string }
  /** Acrescenta texto ao balão corrente (streaming de palavra ou de delta). */
  | { type: 'appendText'; speaker: Speaker; text: string; at: string }
  /** Substitui o texto do balão corrente — usado pelo roteiro palavra a palavra. */
  | { type: 'setTurnText'; text: string }
  | { type: 'setTurnMarks'; marks: Mark[] }
  | { type: 'applyEffect'; effect: Effect; at: string }
  | { type: 'addArtifact'; artifact: Artifact }
  | {
      type: 'patchArtifact'
      id: string
      patch: { status?: ArtifactStatus; status_label?: string; progress_pct?: number; progress_label?: string }
    }
  | { type: 'addAudit'; entry: AuditEntry }
  | { type: 'toggleHuman' }
  | { type: 'toggleMute' }
  | { type: 'setModality'; modality: Modality; capPct: number }

export function callReducer(state: CallState, action: CallAction): CallState {
  switch (action.type) {
    case 'reset':
      return { ...initialCallState, guardrails: action.guardrails, capPct: action.capPct }

    case 'start':
      return {
        ...initialCallState,
        phase: 'live',
        orb: 'think',
        guardrails: action.guardrails,
        capPct: action.capPct,
      }

    case 'end':
      return { ...state, phase: 'ended', orb: 'idle' }

    case 'tick':
      if (state.phase !== 'live') return state
      return {
        ...state,
        elapsed: state.elapsed + 1,
        costBrl: state.costBrl + 0.0043,
        jitterMs: Math.round((Math.random() - 0.5) * 26),
      }

    case 'syncElapsed':
      return {
        ...state,
        elapsed: action.elapsed,
        costBrl: initialCallState.costBrl + action.elapsed * 0.0043,
      }

    case 'orb':
      return { ...state, orb: action.state }

    case 'beginTurn':
      return {
        ...state,
        turns: [...state.turns, { speaker: action.speaker, text: '', at: action.at, marks: [] }],
        orb: action.speaker === 'ai' ? 'ai' : 'cli',
      }

    case 'appendText': {
      const turns = state.turns.slice()
      const last = turns[turns.length - 1]
      if (last && last.speaker === action.speaker) {
        turns[turns.length - 1] = { ...last, text: last.text + action.text }
      } else {
        turns.push({ speaker: action.speaker, text: action.text, at: action.at, marks: [] })
      }
      return { ...state, turns, orb: action.speaker === 'ai' ? 'ai' : 'cli' }
    }

    case 'setTurnText': {
      if (state.turns.length === 0) return state
      const turns = state.turns.slice()
      turns[turns.length - 1] = { ...turns[turns.length - 1], text: action.text }
      return { ...state, turns }
    }

    case 'setTurnMarks': {
      if (state.turns.length === 0) return state
      const turns = state.turns.slice()
      turns[turns.length - 1] = { ...turns[turns.length - 1], marks: action.marks }
      return { ...state, turns }
    }

    case 'setModality':
      return { ...state, modality: action.modality, capPct: action.capPct }

    case 'addArtifact':
      return { ...state, artifacts: [...state.artifacts, action.artifact] }

    case 'patchArtifact':
      return {
        ...state,
        artifacts: state.artifacts.map((artifact) =>
          artifact.id === action.id ? { ...artifact, ...action.patch } : artifact,
        ),
      }

    case 'addAudit':
      return { ...state, audit: [...state.audit, action.entry] }

    case 'toggleHuman':
      return { ...state, humanInControl: !state.humanInControl }

    case 'toggleMute':
      return { ...state, muted: !state.muted }

    case 'applyEffect':
      return applyEffect(state, action.effect, action.at)

    default:
      return state
  }
}

function applyEffect(state: CallState, effect: Effect, at: string): CallState {
  const { payload } = effect

  switch (effect.type) {
    case 'set_client_ask': {
      const asked = Number(payload.pct)
      return {
        ...state,
        clientAskPct: asked,
        clientFirstAskPct: state.clientFirstAskPct ?? asked,
      }
    }

    case 'set_modality':
      return {
        ...state,
        modality: payload.modality as Modality,
        capPct: Number(payload.cap_pct ?? state.capPct),
      }

    case 'set_offer': {
      const next = Number(payload.pct)
      // A trilha guarda as ofertas anteriores para mostrar o caminho percorrido
      // dentro do envelope — o "de onde saiu até onde chegou".
      const trail =
        state.offerPct !== null && state.offerPct !== next
          ? [...state.trail, state.offerPct]
          : state.trail
      return { ...state, offerPct: next, trail, offerWall: Boolean(payload.wall) }
    }

    case 'set_proposal':
      return {
        ...state,
        proposal: {
          total: Number(payload.total),
          down_payment: payload.down_payment == null ? null : Number(payload.down_payment),
          installments: payload.installments == null ? null : Number(payload.installments),
          installment_value:
            payload.installment_value == null ? null : Number(payload.installment_value),
          discount_pct: Number(payload.discount_pct),
          first_due_date: (payload.first_due_date as string) ?? null,
          status: String(payload.status),
          within_policy: Boolean(payload.within_policy ?? true),
        },
      }

    case 'set_guardrail':
      return {
        ...state,
        guardrails: state.guardrails.map((guardrail) =>
          guardrail.key === payload.key
            ? { ...guardrail, state: payload.state as GuardrailState['state'] }
            : guardrail,
        ),
      }

    case 'add_reasoning':
      return {
        ...state,
        reasoning: [
          {
            turn: state.reasoning.length + 1,
            at,
            confidence: String(payload.confidence ?? '—'),
            intent: String(payload.intent ?? ''),
            sentiment: String(payload.sentiment ?? ''),
            retrieved_context: String(payload.retrieved_context ?? ''),
            strategy: String(payload.strategy ?? ''),
            discarded: String(payload.discarded ?? ''),
          },
          ...state.reasoning,
        ],
      }

    case 'add_tool_call':
      return {
        ...state,
        toolCalls: [
          {
            id: `${payload.name}-${state.toolCalls.length}`,
            name: String(payload.name),
            arguments: (payload.arguments ?? {}) as Record<string, unknown>,
            result: String(payload.result ?? '200 · 240ms'),
            at,
          },
          ...state.toolCalls,
        ],
      }

    // `add_artifact` e `audit` são assíncronos (batem no backend); o hook os
    // intercepta antes de chegar aqui.
    default:
      return state
  }
}

/** Ritmo de fala do roteiro: pausa maior depois de vírgula e de ponto. */
export function wordDelayMs(word: string, speed: number): number {
  let ms = 235
  if (/[,;:—]$/.test(word)) ms += 190
  if (/[.?!]$/.test(word)) ms += 340
  return ms / speed
}
